import React, { useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { Formik } from 'formik';
import * as Yup from 'yup';

import Button from '~/components/core/Atomic/Buttons/Button';
import CancelButton from '~/components/core/Buttons/CancelButton';
import SearchWithOptionsFormik from '~/components/core/Formik/SearchWithOptionsFormik/SearchWithOptionsFormik';

import { reportAxiosError } from '../../../../../Utils';
import CardDialog from '../../../../CardDialog';
import { SortableTable } from '../../../../core';
import TooltipIcon from '../../../../core/TooltipIcon';
import HoverActionField from '../../../../HoverActionField';
import { PencilIcon } from '../../../../icons';
import InlineIconButton from '../../../../InlineIconButton';
import { useSysconfig } from '../../../SystemConfigurationScreen';
import QueueVoicemailConfigDialog from '../QueueVoicemail/QueueVoicemailConfigDialog';

import { useStyles } from '../../../../../assets/styles';

const MAIN_FIELD_ID = 'user_sids';

const EditAdjustersInQueue = ({ queue, users, onSubmit, onCancel }) => {
  const { classes } = useStyles();

  const options = [
    {
      entries: users.map((user) => ({
        id: user.sid,
        entryTitle: user.name,
        isDisabled: false,
      })),
    },
  ];

  return (
    <Formik
      initialValues={{
        [MAIN_FIELD_ID]: queue.workers.map((worker) => worker.sid),
      }}
      validationSchema={Yup.object().shape({
        [MAIN_FIELD_ID]: Yup.array().of(Yup.string()),
      })}
      onSubmit={async (values, formikProps) => {
        try {
          await onSubmit({ queue_sid: queue.queue_sid, user_sids: values.user_sids });
          onCancel();
        } catch {
          formikProps.setSubmitting(false);
        }
      }}
    >
      {({ isSubmitting, handleSubmit }) => {
        return (
          <CardDialog
            title={`Assign Adjusters to Queue ${queue.queue_name}`}
            isDialog
            maxWidth="sm"
            fullWidth
            onClose={onCancel}
            preventClose={isSubmitting}
          >
            <SearchWithOptionsFormik
              isSelectAllEnabled={false}
              options={options}
              mainFieldId={MAIN_FIELD_ID}
              label="Search Queues"
            />
            <div className={classes.buttonsContainer}>
              <CancelButton disabled={isSubmitting} onClick={onCancel} />
              <Button variant="contained" color="primary" disabled={isSubmitting} onClick={handleSubmit}>
                Save
              </Button>
            </div>
          </CardDialog>
        );
      }}
    </Formik>
  );
};

EditAdjustersInQueue.propTypes = {
  queue: PropTypes.object,
  users: PropTypes.array,
  onSubmit: PropTypes.func,
  onCancel: PropTypes.func,
};

const queueViewColumnsData = (setDialogData, setVoicemailDialogQueue, voicemailEnabled) => [
  { id: 'queue_name', label: 'Queue name' },
  {
    id: 'workers_count',
    label: 'Assigned Users',
    specialCell: (row) => {
      return (
        <HoverActionField
          onAction={() => {
            setDialogData(row);
          }}
          permanent
        >
          {row.workers_count}
        </HoverActionField>
      );
    },
  },
  {
    id: 'voicemail',
    label: 'Voicemail',
    specialCell: (row) =>
      voicemailEnabled ? (
        <TooltipIcon title="Configure per-queue voicemail override">
          <InlineIconButton icon={PencilIcon} onClick={() => setVoicemailDialogQueue(row)} />
        </TooltipIcon>
      ) : null,
  },
];

const QueueViewTable = ({ data, onSubmit }) => {
  const [dialogData, setDialogData] = useState(false);
  const [voicemailDialogQueue, setVoicemailDialogQueue] = useState(null);
  const [voicemailDialogExisting, setVoicemailDialogExisting] = useState(null);
  const [voicemailDialogLoading, setVoicemailDialogLoading] = useState(false);
  const { organization, organizationOperationalDetails } = useSysconfig();
  const voicemailEnabled = !!organizationOperationalDetails?.twilioConfiguration?.queue_voicemail_enabled;

  const openVoicemailDialog = async (queue) => {
    setVoicemailDialogExisting(null);
    setVoicemailDialogLoading(true);
    setVoicemailDialogQueue(queue);
    try {
      const { data: existing } = await axios.get(
        `/api/v1/organizations/${organization.id}/queues/${queue.queue_sid}/voicemail_configuration`,
        { validateStatus: (s) => s === 200 || s === 404 }
      );
      setVoicemailDialogExisting(existing || null);
    } catch (error) {
      reportAxiosError(error);
    } finally {
      setVoicemailDialogLoading(false);
    }
  };

  const saveVoicemailOverride = async (payload) => {
    try {
      await axios.put(
        `/api/v1/organizations/${organization.id}/queues/${voicemailDialogQueue.queue_sid}/voicemail_configuration`,
        payload
      );
    } catch (error) {
      reportAxiosError(error);
      throw error;
    }
  };

  const clearVoicemailOverride = async () => {
    try {
      await axios.delete(
        `/api/v1/organizations/${organization.id}/queues/${voicemailDialogQueue.queue_sid}/voicemail_configuration`
      );
    } catch (error) {
      reportAxiosError(error);
      throw error;
    }
  };

  return (
    <>
      <CardDialog title="Queue View" maxWidth="lg">
        <div>
          <SortableTable
            columns={queueViewColumnsData(
              setDialogData,
              (q) => {
                openVoicemailDialog(q);
              },
              voicemailEnabled
            )}
            rows={data.queues}
          />
        </div>
      </CardDialog>
      {dialogData ? (
        <EditAdjustersInQueue
          queue={dialogData}
          users={data.users}
          onCancel={() => setDialogData(null)}
          onSubmit={onSubmit}
        />
      ) : null}
      {voicemailDialogQueue ? (
        <QueueVoicemailConfigDialog
          queueName={voicemailDialogQueue.queue_name}
          existing={voicemailDialogExisting}
          loading={voicemailDialogLoading}
          onSubmit={saveVoicemailOverride}
          onClearAll={clearVoicemailOverride}
          onCancel={() => {
            setVoicemailDialogQueue(null);
            setVoicemailDialogExisting(null);
          }}
        />
      ) : null}
    </>
  );
};

QueueViewTable.propTypes = {
  data: PropTypes.object,
  onSubmit: PropTypes.func,
};

export default QueueViewTable;
